import React from 'react';
import { ModalShell } from './ModalShell';
import { Edit3, X, Save } from 'lucide-react';
import { Prompt } from '../types';

interface PromptEditorModalProps {
  prompt: Prompt;
  onChange: (updatedPrompt: Prompt) => void;
  onSave: () => void;
  onCancel: () => void;
}

export const PromptEditorModal: React.FC<PromptEditorModalProps> = ({
  prompt,
  onChange,
  onSave,
  onCancel
}) => {
  return (
    <ModalShell labelledBy="prompt-editor-title" layer="modal-top" panelClassName="max-w-[1100px] max-h-[92vh] p-5 sm:p-8">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <h2 id="prompt-editor-title" style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Edit3 style={{ width: '20px', height: '20px', color: 'var(--accent-strong)' }} />
            {prompt.isCustom ? 'Edycja szablonu AI' : 'Szczegóły szablonu AI'}
          </h2>
          <button
            onClick={onCancel}
            style={{
              padding: '8px',
              backgroundColor: 'color-mix(in srgb, var(--error) 12%, transparent)',
              color: 'var(--error)',
              border: '1px solid color-mix(in srgb, var(--error) 35%, transparent)',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--error) 24%, transparent)')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--error) 12%, transparent)')}
          >
            <X style={{ width: '16px', height: '16px' }} />
          </button>
        </div>

        {/* Inputs row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px' }}>
              Nazwa szablonu:
            </label>
            <input
              type="text"
              value={prompt.name}
              disabled={!prompt.isCustom}
              onChange={(e) => onChange({ ...prompt, name: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 14px',
                backgroundColor: prompt.isCustom ? 'var(--surface-card)' : 'var(--surface-subtle)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'var(--text-primary)',
                fontWeight: '500',
                cursor: prompt.isCustom ? 'text' : 'not-allowed',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                outline: 'none'
              }}
              onFocus={(e) => {
                if (prompt.isCustom) {
                  e.target.style.borderColor = 'var(--accent)';
                  e.target.style.boxShadow = '0 0 0 4px rgba(5, 150, 105, 0.10)';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          <div style={{ flex: '2 1 260px', minWidth: 0 }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px' }}>
              Krótki opis przeznaczenia:
            </label>
            <input
              type="text"
              value={prompt.description}
              disabled={!prompt.isCustom}
              onChange={(e) => onChange({ ...prompt, description: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 14px',
                backgroundColor: prompt.isCustom ? 'var(--surface-card)' : 'var(--surface-subtle)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'var(--text-primary)',
                fontWeight: '500',
                cursor: prompt.isCustom ? 'text' : 'not-allowed',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                outline: 'none'
              }}
              onFocus={(e) => {
                if (prompt.isCustom) {
                  e.target.style.borderColor = 'var(--accent)';
                  e.target.style.boxShadow = '0 0 0 4px rgba(5, 150, 105, 0.10)';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* Content area */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          marginBottom: '24px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '24px'
        }}>
          {/* Main prompt editor */}
          <div style={{ flex: '3 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px' }}>
              Treść instrukcji systemowej (Prompt):
            </label>
            <textarea
              value={prompt.prompt}
              disabled={!prompt.isCustom}
              onChange={(e) => onChange({ ...prompt, prompt: e.target.value })}
              style={{
                width: '100%',
                flex: 1,
                minHeight: '280px',
                padding: '16px',
                backgroundColor: prompt.isCustom ? 'var(--surface-subtle)' : 'var(--surface-subtle)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                fontSize: '13px',
                fontFamily: 'monospace',
                lineHeight: '1.6',
                color: 'var(--text-secondary)',
                resize: 'none',
                outline: 'none',
                cursor: prompt.isCustom ? 'text' : 'not-allowed',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
                transition: 'border-color 0.2s, background-color 0.2s, box-shadow 0.2s'
              }}
              onFocus={(e) => {
                if (prompt.isCustom) {
                  e.target.style.borderColor = 'var(--accent)';
                  e.target.style.backgroundColor = 'var(--surface-card)';
                  e.target.style.boxShadow = 'inset 0 1px 2px rgba(0,0,0,0.01), 0 0 0 4px rgba(5, 150, 105, 0.10)';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border)';
                e.target.style.backgroundColor = prompt.isCustom ? 'var(--surface-subtle)' : 'var(--surface-subtle)';
                e.target.style.boxShadow = 'inset 0 1px 2px rgba(0,0,0,0.02)';
              }}
              placeholder="Wprowadź treść promptu..."
            />
          </div>

          {/* Placeholders helper */}
          <div style={{
            flex: '1 1 260px',
            minWidth: 0,
            padding: '20px',
            backgroundColor: 'var(--surface-subtle)',
            borderRadius: '12px',
            border: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto'
          }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              margin: '0 0 8px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              📝 Dostępne placeholdery
            </h3>

            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: '1.4' }}>
              {prompt.isCustom 
                ? 'Kliknij element, aby wstawić zmienną w miejscu kursora:' 
                : 'Użyte zmienne dynamiczne w szablonie:'}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {[
                { placeholder: '{PATIENT_NAME}', description: 'Imię dziecka' },
                { placeholder: '{VCI_SCORE}', description: 'Rozumienie Słowne (VCI)' },
                { placeholder: '{VSI_SCORE}', description: 'Przestrzenny (VSI)' },
                { placeholder: '{FRI_SCORE}', description: 'Rozumowanie Płynne (FRI)' },
                { placeholder: '{WMI_SCORE}', description: 'Pamięć Robocza (WMI)' },
                { placeholder: '{PSI_SCORE}', description: 'Szybkość Przetwarzania (PSI)' },
                { placeholder: '{FSIQ_SCORE}', description: 'Pełnoskalowy IQ (FSIQ)' },
                { placeholder: '{SUBTEST_SCORES}', description: 'Wyniki wszystkich podtestów' }
              ].map((item, index) => (
                <div
                  key={index}
                  onClick={() => {
                    if (!prompt.isCustom) return;
                    const textarea = document.querySelector('textarea');
                    if (textarea) {
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const text = textarea.value;
                      const newText = text.substring(0, start) + item.placeholder + text.substring(end);
                      onChange({ ...prompt, prompt: newText });
                      setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(start + item.placeholder.length, start + item.placeholder.length);
                      }, 0);
                    }
                  }}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: 'var(--surface-card)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    cursor: prompt.isCustom ? 'pointer' : 'default',
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.01)'
                  }}
                  onMouseOver={(e) => {
                    if (prompt.isCustom) {
                      e.currentTarget.style.backgroundColor = 'var(--surface-subtle)';
                      e.currentTarget.style.borderColor = 'var(--border-strong)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (prompt.isCustom) {
                      e.currentTarget.style.backgroundColor = 'var(--surface-card)';
                      e.currentTarget.style.borderColor = 'var(--border)';
                    }
                  }}
                >
                  <div style={{
                    fontFamily: 'monospace',
                    fontSize: '11px',
                    color: 'var(--accent)',
                    fontWeight: '600'
                  }}>
                    {item.placeholder}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    {item.description}
                  </div>
                </div>
              ))}
            </div>

            {prompt.isCustom && (
              <div style={{
                padding: '12px',
                backgroundColor: 'color-mix(in srgb, var(--warning) 10%, transparent)',
                border: '1px solid color-mix(in srgb, var(--warning) 25%, transparent)',
                borderRadius: '12px',
                fontSize: '11px',
                color: 'var(--warning)',
                lineHeight: '1.4'
              }}>
                💡 <strong>Wskazówka:</strong> Szablony interpretują wyniki psychometryczne w locie podstawiając te wartości.
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              backgroundColor: 'var(--surface-card)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--surface-subtle)')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'var(--surface-card)')}
          >
            {prompt.isCustom ? 'Anuluj' : 'Zamknij'}
          </button>

          {prompt.isCustom && (
            <button
              onClick={onSave}
              style={{
                padding: '10px 20px',
                backgroundColor: 'var(--accent-strong)',
                color: 'var(--text-on-accent)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.15)',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-strong)')}
            >
              <Save style={{ width: '16px', height: '16px' }} />
              Zapisz zmiany
            </button>
          )}
        </div>
    </ModalShell>
  );
};
